/**
 * SamvadOnboarding — Program registration form
 * 3-step wizard: Personal → Location & Level → Preferences & Terms
 * Safeguarding: guardian fields appear only when age < 18.
 * Age is metadata only — not used for knowledge gating (adhikara domain rule).
 *
 * Firebase integration: if user is signed in via Firebase, the form is
 * pre-filled and firebase_uid is linked to the participant record.
 * Re-visiting this page after registration auto-redirects to dashboard.
 */
import { useState, useCallback, useEffect } from 'react';
import '../styles/SamvadOnboarding.css';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';

interface Props {
  onNavigate: (path: string) => void;
}

interface FormState {
  // Step 1
  full_name: string;
  email: string;
  age: string;
  guardian_name: string;
  guardian_email: string;
  // Step 2
  pincode: string;
  city: string;
  proficiency: number;
  // Step 3
  preferred_times: string;
  archetype: string;
  terms_accepted: boolean;
}

const PROFICIENCY_LABELS: Record<number, { hi: string; en: string }> = {
  1: { hi: 'कोई ज्ञान नहीं', en: 'No knowledge' },
  2: { hi: 'कुछ मूल वर्णमाला', en: 'Basic alphabet only' },
  3: { hi: 'सरल वाक्य बोल सकता हूँ', en: 'Simple sentences' },
  4: { hi: 'संवाद समझता/समझती हूँ', en: 'Can hold a conversation' },
  5: { hi: 'धाराप्रवाह', en: 'Fluent' },
};

const ARCHETYPE_OPTIONS = [
  { value: '', labelHi: 'पता नहीं / अभी नहीं', labelEn: 'Unknown / not yet' },
  { value: 'BRAHMIN',   labelHi: 'ब्राह्मण',   labelEn: 'Brahmin — knowledge & teaching' },
  { value: 'KSHATRIYA', labelHi: 'क्षत्रिय',  labelEn: 'Kṣatriya — leadership & protection' },
  { value: 'VAISHYA',   labelHi: 'वैश्य',      labelEn: 'Vaiśya — exchange & creation' },
  { value: 'SHUDRA',    labelHi: 'शूद्र',      labelEn: 'Śūdra — craft & refined service' },
];

const INITIAL: FormState = {
  full_name: '', email: '', age: '', guardian_name: '', guardian_email: '',
  pincode: '', city: '', proficiency: 0,
  preferred_times: '', archetype: '', terms_accepted: false,
};

export default function SamvadOnboarding({ onNavigate }: Props) {
  const { user, authLoading } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ participant_id: string; safeguarding_flag: boolean } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);

  // Pre-fill from Firebase and check for existing registration
  useEffect(() => {
    if (!user) return;
    // Pre-fill name/email from Firebase identity
    setForm(f => ({
      ...f,
      full_name: f.full_name || (user.displayName ?? ''),
      email:     f.email     || (user.email     ?? ''),
    }));
    // Check if already registered — skip form if so
    setCheckingExisting(true);
    fetch(`/api/samvad/me/${encodeURIComponent(user.uid)}`)
      .then(r => r.ok ? r.json() as Promise<{ participant_id: string; safeguarding_flag: boolean }> : null)
      .then(data => {
        if (data?.participant_id) {
          localStorage.setItem('samvad_participant_id', data.participant_id);
          setResult(data);
        }
      })
      .catch(() => {/* not registered yet — show form */})
      .finally(() => setCheckingExisting(false));
  }, [user]);

  const ageNum = parseInt(form.age, 10);
  const isMinor = !isNaN(ageNum) && ageNum < 18;

  const set = useCallback((field: keyof FormState, value: string | boolean | number) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => { const next = { ...e }; delete next[field]; return next; });
  }, []);

  // ── Step validators ──────────────────────────────────────────────────────
  function validateStep1(): boolean {
    const errs: typeof errors = {};
    if (!form.full_name.trim()) errs.full_name = 'नाम आवश्यक है';
    const a = parseInt(form.age, 10);
    if (isNaN(a) || a < 9) errs.age = 'आयु 9 वर्ष या अधिक होनी चाहिए';
    if (!isNaN(a) && a < 18) {
      if (!form.guardian_name.trim()) errs.guardian_name = 'अभिभावक का नाम आवश्यक है';
      if (!form.guardian_email.trim()) errs.guardian_email = 'अभिभावक का ईमेल आवश्यक है';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function validateStep2(): boolean {
    const errs: typeof errors = {};
    if (!/^\d{4,10}$/.test(form.pincode.trim())) errs.pincode = 'पिनकोड 4–10 अंक होना चाहिए';
    if (!form.city.trim()) errs.city = 'नगर/ग्राम आवश्यक है';
    if (form.proficiency < 1 || form.proficiency > 5) errs.proficiency = 'कृपया एक स्तर चुनें';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function validateStep3(): boolean {
    const errs: typeof errors = {};
    if (!form.terms_accepted) errs.terms_accepted = 'शर्तें स्वीकार करना आवश्यक है';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  }

  async function handleSubmit() {
    if (!validateStep3()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const payload: Record<string, unknown> = {
        full_name:       form.full_name.trim(),
        email:           form.email.trim() || null,
        firebase_uid:    user?.uid ?? null,
        age:             parseInt(form.age, 10),
        pincode:         form.pincode.trim(),
        city:            form.city.trim(),
        proficiency:     form.proficiency,
        preferred_times: form.preferred_times.trim() || null,
        archetype:       form.archetype || null,
        terms_accepted:  true,
      };
      if (isMinor) {
        payload.guardian_name  = form.guardian_name.trim();
        payload.guardian_email = form.guardian_email.trim();
      }
      const res = await fetch('/api/samvad/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { detail?: string }).detail ?? `Server error ${res.status}`);
      }
      const data = await res.json() as { participant_id: string; safeguarding_flag: boolean };
      localStorage.setItem('samvad_participant_id', data.participant_id);
      setResult(data);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'नेटवर्क त्रुटि — पुनः प्रयास करें');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Auth loading ─────────────────────────────────────────────────────────
  if (authLoading || checkingExisting) {
    return (
      <div className="svo-page">
        <div className="svo-loading-state">
          <div className="svo-loading-spinner" />
          <p>लोड हो रहा है… / Loading…</p>
        </div>
      </div>
    );
  }

  // ── Firebase sign-in gate ─────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="svo-page">
        <div className="svo-auth-gate">
          <div className="svo-auth-icon">🔐</div>
          <h2 className="svo-auth-title">साइन इन करें</h2>
          <p className="svo-auth-sub">
            पंजीकरण के लिए Google या X/Twitter से साइन इन करें।
            <br />
            <em>Sign in with Google or X to register for the program.</em>
          </p>
          <button
            className="svo-btn-primary svo-signin-btn"
            onClick={() => setShowLogin(true)}
          >
            🔑 साइन इन करें · Sign In
          </button>
          <button className="svo-btn-secondary" onClick={() => onNavigate('/samvad')}>
            ← वापस
          </button>
        </div>
        {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      </div>
    );
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (result) {
    const isAlreadyRegistered = !!localStorage.getItem('samvad_participant_id');
    return (
      <div className="svo-page">
        <div className="svo-success">
          <div className="svo-success-icon">{isAlreadyRegistered ? '✅' : '🎉'}</div>
          <h2>{isAlreadyRegistered ? 'आप पहले से पंजीकृत हैं!' : 'पंजीकरण सफल!'}</h2>
          <p className="svo-success-sub">
            {isAlreadyRegistered ? 'Already registered' : 'Registration successful'}
          </p>
          <div className="svo-participant-id">
            <span className="svo-pid-label">आपका प्रतिभागी ID</span>
            <code className="svo-pid-code">{result.participant_id}</code>
            {user && (
              <span className="svo-pid-hint svo-pid-firebase">
                🔗 आपके Google/X खाते से जुड़ा हुआ — किसी भी उपकरण पर स्वतः लोड होगा।
              </span>
            )}
            {!user && (
              <span className="svo-pid-hint">कृपया इसे सुरक्षित रखें — सत्र लॉग करते समय इसकी आवश्यकता होगी।</span>
            )}
          </div>
          {result.safeguarding_flag && (
            <div className="svo-minor-notice">
              <span>🔒</span>
              <p>
                आपकी आयु 18 वर्ष से कम है। अभिभावक की दृश्यता नीति लागू होगी।
                <br />
                <em>Guardian visibility policy applies for participants under 18.</em>
              </p>
            </div>
          )}
          <div className="svo-success-actions">
            <button className="svo-btn-primary" onClick={() => onNavigate('/samvad/dashboard')}>
              📊 मेरा डैशबोर्ड देखें
            </button>
            <button className="svo-btn-secondary" onClick={() => onNavigate('/samvad/session-log')}>
              📝 पहला सत्र लॉग करें
            </button>
            <button className="svo-btn-secondary" onClick={() => onNavigate('/samvad')}>
              ← वापस जाएँ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step progress bar ────────────────────────────────────────────────────
  const stepLabels = ['नाम एवं आयु', 'स्थान एवं स्तर', 'समय एवं शर्तें'];

  return (
    <div className="svo-page">
      <button className="svo-back-btn" onClick={() => step === 1 ? onNavigate('/samvad') : setStep(s => (s - 1) as 1 | 2 | 3)}>
        ← {step === 1 ? 'वापस' : 'पिछला चरण'}
      </button>

      <div className="svo-card">
        {/* Header */}
        <div className="svo-card-header">
          <div className="svo-header-badge">संस्कृत युवा संवाद कार्यक्रम</div>
          <h1 className="svo-card-title">पंजीकरण</h1>
          <p className="svo-card-sub">Program Registration</p>
        </div>

        {/* Step indicator */}
        <div className="svo-steps">
          {stepLabels.map((label, idx) => (
            <div
              key={idx}
              className={`svo-step ${step === idx + 1 ? 'svo-step--active' : ''} ${step > idx + 1 ? 'svo-step--done' : ''}`}
            >
              <div className="svo-step-dot">{step > idx + 1 ? '✓' : idx + 1}</div>
              <span className="svo-step-label">{label}</span>
            </div>
          ))}
          <div className="svo-step-line" style={{ width: `${((step - 1) / 2) * 100}%` }} />
        </div>

        {/* ── Step 1 ── */}
        {step === 1 && (
          <div className="svo-form">
            <div className="svo-field">
              <label className="svo-label">पूरा नाम <span className="svo-req">*</span></label>
              <input
                className={`svo-input ${errors.full_name ? 'svo-input--error' : ''}`}
                placeholder="आपका पूरा नाम / Full name"
                value={form.full_name}
                onChange={e => set('full_name', e.target.value)}
                autoFocus
              />
              {errors.full_name && <span className="svo-error">{errors.full_name}</span>}
            </div>

            <div className="svo-field">
              <label className="svo-label">ईमेल <span className="svo-hint-inline">(वैकल्पिक — केवल खाता-पुनर्प्राप्ति हेतु)</span></label>
              <input
                className="svo-input"
                type="email"
                placeholder="your@email.com (optional)"
                value={form.email}
                onChange={e => set('email', e.target.value)}
              />
            </div>

            <div className="svo-field">
              <label className="svo-label">आयु (वर्ष) <span className="svo-req">*</span></label>
              <input
                className={`svo-input svo-input--short ${errors.age ? 'svo-input--error' : ''}`}
                type="number" min={9} max={27} placeholder="9–27"
                value={form.age}
                onChange={e => set('age', e.target.value)}
              />
              <span className="svo-hint">आयु केवल सुरक्षा-नीति हेतु — ज्ञान-प्रवेश के लिए नहीं</span>
              {errors.age && <span className="svo-error">{errors.age}</span>}
            </div>

            {isMinor && (
              <div className="svo-minor-section">
                <div className="svo-minor-banner">
                  🔒 अल्पवयस्क प्रतिभागी — अभिभावक विवरण आवश्यक
                  <span className="svo-minor-banner-en">Minor participant — guardian details required</span>
                </div>
                <div className="svo-field">
                  <label className="svo-label">अभिभावक का नाम <span className="svo-req">*</span></label>
                  <input
                    className={`svo-input ${errors.guardian_name ? 'svo-input--error' : ''}`}
                    placeholder="माता/पिता/संरक्षक का नाम"
                    value={form.guardian_name}
                    onChange={e => set('guardian_name', e.target.value)}
                  />
                  {errors.guardian_name && <span className="svo-error">{errors.guardian_name}</span>}
                </div>
                <div className="svo-field">
                  <label className="svo-label">अभिभावक का ईमेल <span className="svo-req">*</span></label>
                  <input
                    className={`svo-input ${errors.guardian_email ? 'svo-input--error' : ''}`}
                    type="email" placeholder="guardian@example.com"
                    value={form.guardian_email}
                    onChange={e => set('guardian_email', e.target.value)}
                  />
                  {errors.guardian_email && <span className="svo-error">{errors.guardian_email}</span>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2 ── */}
        {step === 2 && (
          <div className="svo-form">
            <div className="svo-row">
              <div className="svo-field">
                <label className="svo-label">पिनकोड / Zipcode <span className="svo-req">*</span></label>
                <input
                  className={`svo-input svo-input--short ${errors.pincode ? 'svo-input--error' : ''}`}
                  placeholder="110001"
                  value={form.pincode}
                  onChange={e => set('pincode', e.target.value)}
                  autoFocus
                />
                {errors.pincode && <span className="svo-error">{errors.pincode}</span>}
              </div>
              <div className="svo-field">
                <label className="svo-label">नगर / ग्राम <span className="svo-req">*</span></label>
                <input
                  className={`svo-input ${errors.city ? 'svo-input--error' : ''}`}
                  placeholder="दिल्ली / Delhi"
                  value={form.city}
                  onChange={e => set('city', e.target.value)}
                />
                {errors.city && <span className="svo-error">{errors.city}</span>}
              </div>
            </div>

            <div className="svo-field">
              <label className="svo-label">संस्कृत स्तर <span className="svo-req">*</span></label>
              <span className="svo-hint">अभी आप संस्कृत में कहाँ हैं? ईमानदारी से चुनें।</span>
              {errors.proficiency && <span className="svo-error">{errors.proficiency}</span>}
              <div className="svo-proficiency-grid">
                {([1, 2, 3, 4, 5] as const).map(level => (
                  <button
                    key={level}
                    type="button"
                    className={`svo-prof-btn ${form.proficiency === level ? 'svo-prof-btn--selected' : ''}`}
                    onClick={() => set('proficiency', level)}
                  >
                    <span className="svo-prof-num">{level}</span>
                    <span className="svo-prof-hi">{PROFICIENCY_LABELS[level].hi}</span>
                    <span className="svo-prof-en">{PROFICIENCY_LABELS[level].en}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3 ── */}
        {step === 3 && (
          <div className="svo-form">
            <div className="svo-field">
              <label className="svo-label">पसंदीदा समय (वैकल्पिक)</label>
              <input
                className="svo-input"
                placeholder="जैसे: सुबह 7–9, शाम 5–7 (IST)"
                value={form.preferred_times}
                onChange={e => set('preferred_times', e.target.value)}
                autoFocus
              />
              <span className="svo-hint">युगलन (partner matching) में सहायता करता है।</span>
            </div>

            <div className="svo-field">
              <label className="svo-label">स्वभाव / Archetype (वैकल्पिक)</label>
              <span className="svo-hint">यदि ज्ञात हो। अनिश्चित हों तो "पता नहीं" चुनें — Acharya परामर्श देंगे।</span>
              <div className="svo-archetype-grid">
                {ARCHETYPE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`svo-arch-btn ${form.archetype === opt.value ? 'svo-arch-btn--selected' : ''}`}
                    onClick={() => set('archetype', opt.value)}
                  >
                    <span className="svo-arch-hi">{opt.labelHi}</span>
                    <span className="svo-arch-en">{opt.labelEn}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="svo-field svo-field--terms">
              <label className="svo-terms-row">
                <input
                  type="checkbox"
                  className="svo-checkbox"
                  checked={form.terms_accepted}
                  onChange={e => set('terms_accepted', e.target.checked)}
                />
                <span>
                  मैं{' '}
                  <button type="button" className="svo-link-btn" onClick={() => onNavigate('/terms')}>
                    कार्यक्रम की शर्तें
                  </button>{' '}
                  स्वीकार करता/करती हूँ।
                  {isMinor && ' अभिभावक की सहमति प्राप्त है।'}
                </span>
              </label>
              {errors.terms_accepted && <span className="svo-error">{errors.terms_accepted}</span>}
            </div>

            {serverError && (
              <div className="svo-server-error">⚠ {serverError}</div>
            )}
          </div>
        )}

        {/* ── Navigation buttons ── */}
        <div className="svo-nav-row">
          {step < 3 ? (
            <button className="svo-btn-primary" onClick={handleNext}>
              अगला चरण →
            </button>
          ) : (
            <button
              className="svo-btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? '…सबमिट हो रहा है' : '✓ पंजीकरण पूर्ण करें'}
            </button>
          )}
        </div>

        <p className="svo-footer-note">
          पहले से पंजीकृत हैं?{' '}
          <button type="button" className="svo-link-btn" onClick={() => onNavigate('/samvad/session-log')}>
            सत्र लॉग करें →
          </button>
        </p>
      </div>
    </div>
  );
}
